/**
 * API client.
 *
 * Same-origin, so cookies are sent automatically and there is no token to store in
 * JavaScript — the session cookie is httpOnly and unreadable by script, which is the point
 * (SPEC §10).
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly retryAfterMinutes?: number,
    /**
     * Who stands in the way of a refused location deactivation (SPEC §4.2).
     *
     * Carried on the error rather than returned as a normal result because it IS the
     * refusal: "you cannot do this" and "here is exactly what to fix" are one message, and
     * splitting them leaves the UI showing a dead end.
     */
    public readonly blockers?: LocationBlocker[],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * What to say when the server did not say anything usable.
 *
 * Always names the STATUS, because "Something went wrong" is the same sentence for a
 * misrouted request, a cold container and a server crash — and an instructor who reports
 * that sentence has told us nothing. It also decides whether retrying is worth their time,
 * which is the only thing they can actually act on.
 */
function fallbackMessage(status: number): string {
  if (status === 0) {
    return "We couldn't reach the portal. Check your connection and try again.";
  }
  if (status === 403) {
    return 'That request was refused (403). Open the portal from its own address and try again.';
  }
  if (status === 404) {
    return 'That page is no longer there (404). Open the portal from its own address.';
  }
  if (status >= 500) {
    return `Something went wrong at our end (${status}). Please try again in a moment.`;
  }
  return `That didn't work (${status}). Please try again.`;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      credentials: 'same-origin',
      headers: {
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...init.headers,
      },
    });
  } catch {
    // A network failure is an ApiError too, so callers have exactly ONE failure type and
    // their `err instanceof ApiError` fallback stops being the thing that eats the detail.
    throw new ApiError(0, 'network', fallbackMessage(0));
  }

  const text = await response.text();

  // Parsed defensively, and AFTER the body is in hand rather than before the status is
  // checked. The old order threw a SyntaxError on any non-JSON error page — Cloud Run's 5xx
  // HTML, a proxy notice — before `ApiError` was ever constructed, so the status was lost
  // and every one of them reached the instructor as a bare "Something went wrong".
  let body: Record<string, unknown> = {};
  if (text) {
    try {
      body = JSON.parse(text) as Record<string, unknown>;
    } catch {
      body = {};
    }
  }

  if (!response.ok) {
    throw new ApiError(
      response.status,
      String(body.error ?? 'unknown'),
      // Server messages are written for instructors, not developers, so they are shown
      // as-is. The fallback names the status, so an unexplained refusal is still reportable.
      typeof body.message === 'string' && body.message.length > 0
        ? body.message
        : fallbackMessage(response.status),
      typeof body.retryAfterMinutes === 'number' ? body.retryAfterMinutes : undefined,
      Array.isArray(body.blockers) ? (body.blockers as LocationBlocker[]) : undefined,
    );
  }

  return body as T;
}

const get = <T,>(path: string) => request<T>(path);
const post = <T,>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });
const put = <T,>(path: string, body: unknown) =>
  request<T>(path, { method: 'PUT', body: JSON.stringify(body) });

// ---------------------------------------------------------------------------
// Types mirroring the server responses
// ---------------------------------------------------------------------------

export type Period = 'AM' | 'PM' | 'ALL_DAY';
export type Weekday =
  | 'SUNDAY' | 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY';

export interface Occurrence {
  date: string;
  weekday: Weekday;
  period: Period;
  startTime?: string;
  locationId: number;
  source: 'PATTERN' | 'ADDITION';
  patternSlotId?: string;
}

export type MonthStatus =
  | 'NO_RESPONSE' | 'PARTIAL' | 'COMPLETE' | 'PENDING_REMAINDER' | 'ON_LEAVE';

export interface MonthView {
  cycleKey: string;
  label: string;
  status: MonthStatus;
  isRequiredMonth: boolean;
  patternApplied: boolean;
  occurrences: Occurrence[];
  /** What was actually sent. Differs from `occurrences` while edits are unsent. */
  submitted: Occurrence[];
  hasUnsentChanges: boolean;
  patternPreview: Occurrence[];
  note: string | null;
  /**
   * The instructor's confirmation horizon (SPEC §3.3.1). Person-level, so the same value
   * arrives on every month; already de-staled by the server.
   */
  confirmedThrough: string | null;
  /** Whether that horizon shadows THIS month — false once overridden by submitting it. */
  horizonApplies: boolean;
  /** True when the horizon covers every day of the month (the look-ahead case). */
  wholeMonthUnconfirmed: boolean;
  /** Confirmed / unconfirmed split of `occurrences` (SPEC §3.3.3). */
  counts: { confirmed: number; unconfirmed: number; total: number };
  editable: boolean;
}

/**
 * The horizon context for a month, for the shared `isProvisional` / `splitByHorizon` helpers.
 *
 * `horizonApplies` is computed on the server and already encodes the §3.3.1 override, so
 * folding it in here means the client never re-implements that rule and cannot disagree with
 * the server about which days are confirmed.
 */
export function horizonContextOf(view: MonthView) {
  return {
    cycleKey: view.cycleKey,
    horizon: view.horizonApplies ? view.confirmedThrough : null,
    submitted: false,
  };
}

export interface SubmitResult extends MonthView {
  /** Dates with no availability left. */
  lost: string[];
  /** Dates still available but at a different time or place. */
  moved: string[];
  added: string[];
}

export interface Instructor {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  onboardedAt: string | null;
  needsOnboarding: boolean;
  hasPattern: boolean;
  homeLocation: { id: number; canonicalName: string; timezone: string } | null;
  additionalLocations: { id: number; canonicalName: string }[];
}

export type Frequency =
  | 'EVERY' | 'FIRST' | 'SECOND' | 'THIRD' | 'FOURTH' | 'LAST' | 'ALTERNATING';

export interface PatternSlotInput {
  weekday: Weekday;
  frequency: Frequency;
  /** Omitted on purpose for ALTERNATING — the server picks one so nobody reasons about parity. */
  anchorDate?: string;
  startTime?: string;
  period?: Period;
  locationId: number;
}

export interface PatternSlot extends PatternSlotInput {
  id: string;
  period: Period;
  effectiveFrom: string;
  locationName: string;
}

export interface Profile {
  firstName: string;
  lastName: string;
  email: string;
  homeLocation: { id: number; canonicalName: string } | null;
  additionalLocations: { id: number; canonicalName: string }[];
}

export interface LocationOption {
  id: number;
  canonicalName: string;
  timezone: string;
  isHome: boolean;
}

export interface ExceptionInput {
  date: string;
  type: 'REMOVAL' | 'ADDITION';
  reason?: string;
  startTime?: string;
  period?: Period;
  locationId?: number;
}

export interface DraftInput {
  exceptions?: ExceptionInput[];
  note?: string | null;
  confirmedThrough?: string | null;
  patternApplied?: boolean;
}


// ---------------------------------------------------------------------------
// Leave of absence — SPEC §3.7
// ---------------------------------------------------------------------------

export interface LeaveView {
  id: string;
  startDate: string;
  endDate: string;
  reason: string | null;
  endedOn: string | null;
  createdBy: string | null;
  endedBy: string | null;
  active: boolean;
  upcoming: boolean;
}

export interface LeaveInput {
  startDate?: string;
  endDate: string;
  reason?: string | null;
}

export const api = {
  signIn: {
    request: (email: string) =>
      post<{ sent: boolean; unrecognized: boolean; message: string; retryAfterMinutes?: number }>(
        '/api/auth/request',
        { email },
      ),
    verifyCode: (email: string, code: string, rememberDevice: boolean) =>
      post<{ ok: true; isFirstSignIn: boolean }>('/api/auth/verify-code', {
        email,
        code,
        rememberDevice,
      }),
    verifyLink: (token: string, rememberDevice: boolean) =>
      post<{ ok: true; isFirstSignIn: boolean }>('/api/auth/verify-link', {
        token,
        rememberDevice,
      }),
    signOut: () => post<{ ok: true }>('/api/auth/signout'),
  },
  me: () => get<{ instructor: Instructor | null }>('/api/auth/me'),
  markOnboarded: () => post<{ ok: true }>('/api/auth/onboarded'),
  pattern: {
    get: () => get<{ slots: PatternSlot[] }>('/api/pattern'),
    // Replaces the whole pattern. See the note in server/src/services/pattern.ts on why
    // this is not a diff.
    set: (slots: PatternSlotInput[]) => put<{ slots: PatternSlot[] }>('/api/pattern', { slots }),
  },
  profile: {
    get: () => get<Profile>('/api/profile'),
    // Notifies Luke; changes nothing (SPEC §11.3). Rejects if it could not be delivered,
    // because the notification is the only record.
    reportCorrection: (message: string) =>
      post<{ ok: true }>('/api/profile/correction', { message }),
  },
  // SPEC §3.7. The instructor's own screen sends only an end date: "pause my availability
  // until {date}" is one question with one answer, and asking somebody to type today's date
  // as a start is a way to get it wrong.
  leave: {
    list: () => get<{ leaves: LeaveView[] }>('/api/profile/leave'),
    start: (input: LeaveInput) => post<{ leave: LeaveView }>('/api/profile/leave', input),
    end: (id: string) => post<{ leave: LeaveView }>(`/api/profile/leave/${id}/end`),
    // "Back on the 20th rather than the 30th", said in advance. `end` means "back today".
    reschedule: (id: string, endDate: string) =>
      put<{ leave: LeaveView }>(`/api/profile/leave/${id}`, { endDate }),
  },
  availability: {
    months: () => get<{ months: MonthView[]; requiredCycle: string | null }>('/api/availability'),
    month: (cycleKey: string) => get<MonthView>(`/api/availability/${cycleKey}`),
    applyPattern: (cycleKey: string) =>
      post<MonthView>(`/api/availability/${cycleKey}/apply-pattern`),
    saveDraft: (cycleKey: string, draft: DraftInput) =>
      put<MonthView>(`/api/availability/${cycleKey}/draft`, draft),
    submit: (cycleKey: string, draft: DraftInput = {}) =>
      post<SubmitResult>(`/api/availability/${cycleKey}/submit`, draft),
    locations: () =>
      get<{
        locations: LocationOption[];
        today: { year: number; month: number; day: number };
        cycles: { cycleKey: string; label: string }[];
      }>('/api/availability/meta/locations'),
  },
};

// ---------------------------------------------------------------------------
// Admin dashboard — SPEC §9.3, §9.4
// ---------------------------------------------------------------------------

export type AdminRole = 'ADMIN' | 'COORDINATOR';

export interface AdminIdentity {
  email: string;
  name: string | null;
  role: AdminRole;
}

export interface CoverageInstructor {
  instructorId: string;
  name: string;
  startTime: string | null;
  period: Period;
  provisional: boolean;
  note: string | null;
}

export interface CoverageCell {
  date: string;
  locationId: number;
  confirmed: number;
  unconfirmed: number;
  level: 'gap' | 'single' | 'covered';
  instructors: CoverageInstructor[];
}

export interface CoverageLocationSummary {
  locationId: number;
  name: string;
  instructorCount: number;
  gaps: number;
  singles: number;
  covered: number;
}

export interface CoverageGrid {
  cycleKey: string;
  label: string;
  dates: string[];
  locations: { id: number; name: string; timezone: string }[];
  cells: Record<string, CoverageCell>;
  summaries: CoverageLocationSummary[];
  totals: { confirmed: number; unconfirmed: number };
  exposed: CoverageLocationSummary[];
}

export interface InstructorRow {
  instructorId: string;
  name: string;
  email: string;
  status: MonthStatus;
  confirmedThrough: string | null;
  confirmed: number;
  unconfirmed: number;
  days: number;
  awayDays: number;
  leaves: { startDate: string; endDate: string; wholeMonth: boolean }[];
  locations: string[];
  hasPattern: boolean;
  note: string | null;
  submittedAt: string | null;
  updatedAt: string | null;
}

export interface CycleSummary {
  cycleKey: string;
  label: string;
  deadline: string;
  daysUntilDeadline: number;
  pastDeadline: boolean;
  total: number;
  counts: Record<MonthStatus, number>;
  complete: number;
  outstanding: number;
}

export const adminApi = {
  // Unauthenticated on purpose: the page needs this to render the sign-in button, and
  // requiring a session to fetch what creates a session is a deadlock.
  config: () =>
    get<{ googleClientId: string | null; domain: string }>('/api/admin/config'),
  me: () => get<{ admin: AdminIdentity | null }>('/api/admin/me'),
  signIn: (idToken: string) =>
    post<{ ok: true; admin: AdminIdentity }>('/api/admin/session', { idToken }),
  signOut: () => post<{ ok: true }>('/api/admin/signout'),
  cycles: () =>
    get<{ required: string; cycles: { cycleKey: string; label: string }[] }>('/api/admin/cycles'),
  cycle: (cycleKey?: string) =>
    get<{ summary: CycleSummary; instructors: InstructorRow[] }>(
      `/api/admin/cycle${cycleKey ? `?cycle=${cycleKey}` : ''}`,
    ),
  coverage: (params: { cycle?: string; period?: Period; locationId?: number } = {}) => {
    const query = new URLSearchParams();
    if (params.cycle) query.set('cycle', params.cycle);
    if (params.period) query.set('period', params.period);
    if (params.locationId) query.set('locationId', String(params.locationId));
    const suffix = query.toString();
    return get<CoverageGrid>(`/api/admin/coverage${suffix ? `?${suffix}` : ''}`);
  },
  remindersStatus: (cycleKey: string) =>
    get<{
      lastReminded: Record<string, { at: string; type: string }>;
      sentToday: number;
      dailyCap: number;
    }>(`/api/admin/reminders/status?cycle=${cycleKey}`),
  sendReminders: (cycleKey: string, instructorIds: string[]) =>
    post<ReminderResult>('/api/admin/reminders', { cycleKey, instructorIds, type: 'MANUAL' }),
};

export interface ReminderOutcome {
  instructorId: string;
  name: string;
  email: string;
  sent: boolean;
  skipped?:
    | 'already_complete'
    | 'on_leave'
    | 'inactive'
    | 'fixture'
    | 'send_failed'
    | 'already_sent';
}

export interface ReminderResult {
  cycleKey: string;
  requested: number;
  sent: number;
  outcomes: ReminderOutcome[];
  sentToday: number;
  dailyCap: number;
}

// ---------------------------------------------------------------------------
// Roster admin — SPEC §9.3
// ---------------------------------------------------------------------------

export interface RosterLocationRef {
  locationId: number;
  name: string;
  seeded: boolean;
}

export interface RosterInstructorRow {
  id: string;
  ahaInstructorId: string | null;
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  isActive: boolean;
  onboardedAt: string | null;
  homeLocationId: number | null;
  homeLocationName: string | null;
  additionalLocations: RosterLocationRef[];
  enrollwareDisplayName: string | null;
  enrollwareAliases: string[];
  patternSlots: number;
  lockedOut: boolean;
  lockoutMinutesRemaining: number;
  lastSignInAt: string | null;
  leaves: { id: string; startDate: string; endDate: string; active: boolean }[];
}

export interface RosterLocationRow {
  id: number;
  canonicalName: string;
  aliases: string[];
  address: string | null;
  city: string | null;
  state: string | null;
  timezone: string;
  isActive: boolean;
  homeInstructors: number;
  assignedInstructors: number;
  patternSlots: number;
}

export interface LocationBlocker {
  instructorId: string;
  name: string;
  reason: 'pattern' | 'home' | 'assigned';
  patternSlots: number;
}

export interface InstructorInput {
  firstName?: string;
  lastName?: string;
  email?: string;
  ahaInstructorId?: string | null;
  homeLocationId?: number | null;
  additionalLocationIds?: number[];
  enrollwareDisplayName?: string | null;
  enrollwareAliases?: string[];
}

export interface LocationInput {
  id?: number;
  canonicalName?: string;
  timezone?: string;
  aliases?: string[];
  address?: string | null;
  city?: string | null;
  state?: string | null;
}

export const rosterApi = {
  list: () =>
    get<{ instructors: RosterInstructorRow[]; locations: RosterLocationRow[] }>(
      '/api/admin/roster',
    ),
  createInstructor: (input: InstructorInput) =>
    post<{ instructor: RosterInstructorRow }>('/api/admin/roster/instructors', input),
  updateInstructor: (id: string, patch: InstructorInput) =>
    put<{ instructor: RosterInstructorRow }>(`/api/admin/roster/instructors/${id}`, patch),
  setInstructorActive: (id: string, isActive: boolean) =>
    post<{ instructor: RosterInstructorRow }>(`/api/admin/roster/instructors/${id}/active`, {
      isActive,
    }),
  // Both roles, deliberately — SPEC §11.6 path 3 exists so a locked-out instructor on a
  // Friday does not wait for Luke.
  clearLockout: (id: string) =>
    post<{ cleared: number }>(`/api/admin/roster/instructors/${id}/clear-lockout`),
  createLocation: (input: LocationInput) =>
    post<{ location: RosterLocationRow }>('/api/admin/roster/locations', input),
  updateLocation: (id: number, patch: LocationInput) =>
    put<{ location: RosterLocationRow }>(`/api/admin/roster/locations/${id}`, patch),
  setLocationActive: (id: number, isActive: boolean) =>
    post<{ location: RosterLocationRow }>(`/api/admin/roster/locations/${id}/active`, { isActive }),
  blockers: (id: number) =>
    get<{ blockers: LocationBlocker[] }>(`/api/admin/roster/locations/${id}/blockers`),
  leave: {
    list: (instructorId: string) =>
      get<{ leaves: LeaveView[] }>(`/api/admin/roster/instructors/${instructorId}/leave`),
    start: (instructorId: string, input: LeaveInput) =>
      post<{ leave: LeaveView }>(`/api/admin/roster/instructors/${instructorId}/leave`, input),
    end: (instructorId: string, leaveId: string) =>
      post<{ leave: LeaveView }>(
        `/api/admin/roster/instructors/${instructorId}/leave/${leaveId}/end`,
      ),
    reschedule: (instructorId: string, leaveId: string, endDate: string) =>
      put<{ leave: LeaveView }>(
        `/api/admin/roster/instructors/${instructorId}/leave/${leaveId}`,
        { endDate },
      ),
  },
  reassignLocation: (fromId: number, toLocationId: number) =>
    post<{ patternSlots: number; homes: number; assignments: number }>(
      `/api/admin/roster/locations/${fromId}/reassign`,
      { toLocationId },
    ),
};

// ---------------------------------------------------------------------------
// Entering availability for somebody else — SPEC §9.3
// ---------------------------------------------------------------------------

/**
 * The month-editing surface, so one editor can serve both subjects.
 *
 * `api.availability` already has exactly this shape. The admin version below points at the
 * same handlers mounted under the roster router, which is why the two cannot behave
 * differently: it is the same server code, reached by a different path.
 */
export interface AvailabilityApi {
  months: () => Promise<{ months: MonthView[]; requiredCycle: string | null }>;
  month: (cycleKey: string) => Promise<MonthView>;
  applyPattern: (cycleKey: string) => Promise<MonthView>;
  saveDraft: (cycleKey: string, draft: DraftInput) => Promise<MonthView>;
  submit: (cycleKey: string, draft?: DraftInput) => Promise<SubmitResult>;
  locations: () => Promise<{
    locations: LocationOption[];
    today: { year: number; month: number; day: number };
    cycles: { cycleKey: string; label: string }[];
  }>;
}

/** The same operations, aimed at one instructor, for an admin entering on their behalf. */
export function onBehalfApi(instructorId: string): AvailabilityApi {
  const base = `/api/admin/roster/instructors/${instructorId}/availability`;
  return {
    months: () => get(`${base}`),
    month: (cycleKey) => get(`${base}/${cycleKey}`),
    applyPattern: (cycleKey) => post(`${base}/${cycleKey}/apply-pattern`),
    saveDraft: (cycleKey, draft) => put(`${base}/${cycleKey}/draft`, draft),
    submit: (cycleKey, draft = {}) => post(`${base}/${cycleKey}/submit`, draft),
    locations: () => get(`${base}/meta/locations`),
  };
}

// ---------------------------------------------------------------------------
// Activity and export — SPEC §9.3
// ---------------------------------------------------------------------------

export type ActivitySource = 'ADMIN' | 'NOTIFICATION';
export type ActivityStatus = 'SENT' | 'FAILED' | 'SKIPPED';

export interface ActivityEntry {
  id: string;
  at: string;
  source: ActivitySource;
  /** The raw action or event name. Shown alongside the sentence, never instead of it. */
  key: string;
  /**
   * Plain English, rendered by the SERVER.
   *
   * Deliberately not assembled here: the meaning of an entry lives in the shape of its
   * payload, which is server knowledge, and a second renderer in React would go stale
   * silently — a new action would appear as a blank row, which reads as nothing happening.
   */
  summary: string;
  detail: string | null;
  actor: string | null;
  instructorId: string | null;
  instructorName: string | null;
  channel: 'EMAIL' | 'SLACK' | null;
  status: ActivityStatus | null;
  recipient: string | null;
}

export interface ActivityQuery {
  instructorId?: string;
  actor?: string;
  source?: ActivitySource;
  failedOnly?: boolean;
  search?: string;
  since?: string;
  until?: string;
  limit?: number;
  cursor?: string;
}

function activityQuery(query: ActivityQuery): string {
  const params = new URLSearchParams();
  if (query.instructorId) params.set('instructorId', query.instructorId);
  if (query.actor) params.set('actor', query.actor);
  if (query.source) params.set('source', query.source);
  if (query.failedOnly) params.set('failedOnly', 'true');
  if (query.search) params.set('search', query.search);
  if (query.since) params.set('since', query.since);
  if (query.until) params.set('until', query.until);
  if (query.limit) params.set('limit', String(query.limit));
  if (query.cursor) params.set('cursor', query.cursor);
  return params.toString();
}

export const activityApi = {
  list: (query: ActivityQuery = {}) => {
    const suffix = activityQuery(query);
    return get<{ entries: ActivityEntry[]; nextCursor: string | null }>(
      `/api/admin/activity${suffix ? `?${suffix}` : ''}`,
    );
  },
  actors: () => get<{ actors: string[] }>('/api/admin/activity/actors'),

  /**
   * The download URLs, as links rather than as fetches.
   *
   * A plain link lets the browser handle the download: the file gets its filename from the
   * response header and lands in the downloads folder, and there is no blob to build, name
   * and revoke by hand. The session cookie rides along because it is same-origin.
   */
  activityCsvUrl: (query: ActivityQuery = {}) => {
    const suffix = activityQuery(query);
    return `/api/admin/export/activity.csv${suffix ? `?${suffix}` : ''}`;
  },
  availabilityCsvUrl: (cycleKey: string) =>
    `/api/admin/export/availability.csv?cycle=${encodeURIComponent(cycleKey)}`,
};
